import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { cateringPaths } from '../paths';
import { useCateringOrdersStore } from '../store/useCateringOrders';
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import {
  fetchInvoicesRequest,
  fetchEstimatesRequest,
  fetchCustomersRequest,
  fetchAnalyticsRequest,
} from '../../../redux/catering/cateringActions';
import { useMediaQuery } from '../hooks/useMediaQuery';
import DesktopLayout from '../../layout/DesktopLayout';
import BottomNav from '../../BottomNav';
import { useDesktopTopBar } from '../../../context/DesktopLayoutContext';
import { useCateringAccess } from '../../../redux/catering/cateringAccessHooks';
import { canOpenArea, type CateringArea } from '../utils/cateringAccess';

const MODULES: { icon: string; label: string; desc: string; path: string; color: string; countKey: 'orders' | 'invoices' | 'leads' | 'estimates' | 'customers' | null; area: CateringArea }[] = [
  { icon: 'receipt_long', label: 'Orders', desc: 'View and manage catering orders', path: cateringPaths.orders, color: '#3B82F6', countKey: 'orders', area: 'bookings' },
  { icon: 'calendar_month', label: 'Calendar', desc: 'View orders in calendar format', path: cateringPaths.orders + '?view=month', color: '#7C3AED', countKey: null, area: 'bookings' },
  { icon: 'receipt', label: 'Invoices', desc: 'Generate and track catering invoices', path: cateringPaths.invoices, color: '#0D9488', countKey: 'invoices', area: 'bookings' },
  { icon: 'contact_phone', label: 'Leads', desc: 'Track inquiries and potential clients', path: cateringPaths.leads, color: '#D97706', countKey: 'leads', area: 'bookings' },
  { icon: 'request_quote', label: 'Estimates', desc: 'Create and manage catering quotes', path: cateringPaths.estimates, color: '#8B5CF6', countKey: 'estimates', area: 'bookings' },
  { icon: 'people', label: 'Customers', desc: 'Manage your catering customer base', path: cateringPaths.customers, color: '#16A34A', countKey: 'customers', area: 'customers' },
  // MVP hide — Packages feature hidden for now
  // { icon: 'inventory_2', label: 'Packages', desc: 'Build and price catering packages', path: cateringPaths.packages, color: '#DC2626', countKey: 'packages' as const },
  { icon: 'settings', label: 'Settings', desc: 'Configure catering preferences', path: cateringPaths.settings, color: '#6B7280', countKey: null, area: 'settings' },
];

export default function CateringHubPage() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const dispatch = useAppDispatch();
  /** Only the tiles the employee can open (the page itself is gated on access to any part of Catering). */
  const access = useCateringAccess();
  const modules = MODULES.filter((mod) => canOpenArea(access, mod.area));
  const { total: ordersTotal } = useCateringOrdersStore();
  const invoicesTotal = useAppSelector((s) => s.catering.invoices.total);
  const leadsAnalytics = useAppSelector((s) => s.catering.leads.analytics);
  const estimatesTotal = useAppSelector((s) => s.catering.estimates.total);
  const customersTotal = useAppSelector((s) => s.catering.customers.total);

  useEffect(() => {
    dispatch(fetchInvoicesRequest());
    dispatch(fetchEstimatesRequest());
    dispatch(fetchCustomersRequest());
    dispatch(fetchAnalyticsRequest());
  }, [dispatch]);

  const setTopBarProps = useDesktopTopBar();
  useEffect(() => {
    setTopBarProps({ title: 'Catering', breadcrumbs: '' });
  }, [setTopBarProps]);

  const counts: Record<string, number> = {
    orders: ordersTotal,
    invoices: invoicesTotal,
    leads: leadsAnalytics?.statusCounts.all ?? 0,
    estimates: estimatesTotal,
    customers: customersTotal,
  };

  const content = (
    <div style={{ padding: isMobile ? 16 : 32 }}>
      {isMobile && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 50, background: 'var(--surface, #FFFFFF)',
          borderBottom: '1px solid var(--card-border, #E2E5EC)', padding: '14px 16px',
          margin: '-16px -16px 16px',
        }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--t1, #1A1D2E)', margin: 0, fontFamily: 'var(--font-primary)' }}>Catering</h1>
          <p style={{ fontSize: 12, color: 'var(--t2, #5A6178)', margin: '4px 0 0', fontFamily: 'var(--font-primary)' }}>Manage your catering operations</p>
        </div>
      )}

      {!isMobile && (
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 14, color: 'var(--t2, #5A6178)', margin: 0, fontFamily: 'var(--font-primary)' }}>Manage your entire catering operation from one place</p>
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: isMobile ? 12 : 16,
      }}>
        {modules.map((mod) => (
          <div
            key={mod.label}
            onClick={() => navigate(mod.path)}
            style={{
              background: 'var(--surface, #FFFFFF)',
              borderRadius: 'var(--radius-card, 14px)',
              border: '1px solid var(--card-border, #E2E5EC)',
              padding: isMobile ? '16px 14px' : '20px',
              cursor: 'pointer',
              transition: 'all 0.15s',
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? 10 : 12,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = mod.color;
              (e.currentTarget as HTMLDivElement).style.boxShadow = `0 2px 12px ${mod.color}15`;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--card-border, #E2E5EC)';
              (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12 }}>
              <div style={{
                width: isMobile ? 40 : 44,
                height: isMobile ? 40 : 44,
                borderRadius: 12,
                background: `${mod.color}12`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <span className="material-symbols-rounded" style={{ fontSize: isMobile ? 20 : 22, color: mod.color }}>
                  {mod.icon}
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: isMobile ? 14 : 15,
                  fontWeight: 600,
                  color: 'var(--t1, #1A1D2E)',
                  fontFamily: 'var(--font-primary)',
                  marginBottom: 2,
                }}>
                  {mod.label}
                </div>
                {mod.countKey && (
                  <div style={{ fontSize: 11, color: 'var(--t3, #9CA3B8)', fontWeight: 500, fontFamily: 'var(--font-primary)' }}>
                    {counts[mod.countKey]} {mod.label.toLowerCase()}
                  </div>
                )}
              </div>
            </div>
            <div style={{ fontSize: isMobile ? 11 : 12, color: 'var(--t2, #5A6178)', lineHeight: isMobile ? 1.3 : 1.4, fontFamily: 'var(--font-primary)' }}>
              {mod.desc}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ background: 'var(--bg, #F5F6FA)', minHeight: '100vh', fontFamily: 'var(--font-primary)', paddingBottom: 80 }}>
        {content}
        <BottomNav />
      </div>
    );
  }

  return <DesktopLayout>{content}</DesktopLayout>;
}
